// Company revenue, pivoted.
//
// The open-question sibling of the revenue screen. That one answers "how did
// the company do" with a fixed hierarchy and an opinion; this one answers
// whatever you ask and holds no opinion at all.
//
// SEPARATE SCREEN, SAME SOURCE. Both read signals.company_revenue_hourly
// through the same gate mirror, and the route returns a parity block on every
// request comparing its totals against the live derivation. That block is
// rendered HERE, on the page — a reconciliation nobody can see is one nobody
// checks, and the whole reason this screen is allowed to exist separately is
// that it can prove it agrees with its sibling.
//
// LAYOUT AND SCROLL MODEL ARE BRANDS MANAGER'S REPORTS PAGE. The page fills the
// viewport and never scrolls itself: two compact rows of chrome, then a frame
// holding the table, and inside that frame the only scroller on the screen. The
// column headers, the totals row and the status line all stay put while the
// body moves under them. That is what makes a report usable in one fold, and it
// is impossible while the page container is the scroller — so the shell grew a
// `data-fill` opt-in (see v2/shell/Shell.module.css) rather than this page
// fighting it for the scrollbar.
//
// TWO VIEWS, LIKE BM. "Pivot" nests rows and carries a subtotal at every level;
// "Ungrouped" is one row per combination. The subtotals are NOT folded here —
// they are GROUP BY ROLLUP rows from the same query, because a subtotal's CPM
// is that level's own accrued over that level's own advertiser-facing
// impressions, not an average of its children, and `coverage` cannot be folded
// from the measures the client is given at all. See routes/companyPivot.js.
//
// Not carried across from BM: saved reports. The URL is the saved state here —
// shareable, but not nameable and not listed. Worth building; it needs storage
// this console does not have yet.
//
// The controls are shared with the revenue screen on purpose: the same date
// range component, the same filter component, the same URL parameter names. A
// reader moving between the two screens carries their window and filters with
// them, and never has to wonder whether "last 30 days" means the same thing in
// both places.

import React, { useMemo, useCallback, useState, useEffect, useRef } from 'react';
import { NavLink, useSearchParams } from 'react-router-dom';
import {
  RefreshCw, Download, SlidersHorizontal, Search, X, Info, ChevronDown, ChevronRight,
} from 'lucide-react';
import { Loading, ErrorState, Pill } from '../../components/ui';
import useApi from '../../lib/useApi';
import api from '../../../services/api';
import Filters from './Filters';
import DateRange, { toISO, addDays, startOfDay } from './DateRange';
import CustomizePanel from './CustomizePanel';
import s from './Pivot.module.css';

const SUB = 'The same numbers as Company revenue, cut however you ask. Both screens read '
  + 'signals.company_revenue_hourly through the same gate mirror; this one checks itself '
  + 'against the live derivation on every request and shows the result in the status line below. '
  + 'city and screen are deliberately absent as breakdowns: both resolve through edge_nodes, and '
  + 'every batch fails to resolve, so either would return one "unattributed" bucket holding all '
  + 'the revenue.';

const DIM_LABEL = {
  campaign: 'Campaign', adset: 'Ad set', advertiser: 'Advertiser', status: 'Status',
  day: 'Day', week: 'Week', month: 'Month', hour: 'Hour of day',
};
const DIM_HELP = {
  campaign: 'One row per campaign. Grouped on the id, so a mid-window rename stays one row.',
  adset: 'One row per ad set. An ad set belongs to exactly one campaign.',
  advertiser: 'The tenant that owns the campaign, joined live so renames and re-attribution correct across history.',
  status: 'The campaign’s status right now, joined live — never frozen with the numbers.',
  day: 'IST calendar day, the bucket billing and the signals plane both use.',
  week: 'IST week, starting Monday (date_trunc).',
  month: 'IST calendar month.',
  hour: 'Hour of the IST day, 0–23. Stored as the raw hour so any daypart scheme can be applied later without rebuilding the mart.',
};
const MEA_HELP = {
  spend: 'Rupees accrued, converted from paise at the source.',
  plays: 'Creative plays, summed from impression_count — never a count of batches.',
  billable: 'Plays counting towards an advertiser under billing’s own gate. Overlaps aboard and pre-gate; it is an outcome, not a bucket.',
  occupied: 'Plays where presence was verified.',
  empty: 'Plays verdicted with nobody aboard.',
  cpp: 'Accrued over plays, at this row’s own level.',
  cpm: 'Accrued over ADVERTISER-FACING impressions × 1000, recomputed from each row’s own sums — a subtotal is not the average of its children.',
  cpm_all: 'Accrued over ALL plays × 1000. Diverges from CPM across the gate cutover — that gap is the gate withholding, not revenue falling.',
  empty_rate: 'Empty over (empty + aboard) — verdicted plays only.',
  coverage: 'Share of plays carrying a presence verdict at all. Cannot be folded in the browser: it needs awaiting and pre-gate, so it is computed server-side at every level.',
};

// Sections, so the panel reads as a menu rather than a list. BM groups its
// catalog the same way and for the same reason.
const DIM_SECTIONS = [
  { id: 'level', label: 'Level', keys: ['campaign', 'adset', 'advertiser', 'status'] },
  { id: 'time', label: 'Time', keys: ['day', 'week', 'month', 'hour'] },
];
const MEA_SECTIONS = [
  { id: 'money', label: 'Money', keys: ['spend', 'cpp', 'cpm', 'cpm_all'] },
  { id: 'volume', label: 'Volume', keys: ['plays', 'billable', 'occupied', 'empty'] },
  { id: 'quality', label: 'Quality', keys: ['empty_rate', 'coverage'] },
];

const VIEWS = [
  { id: 'pivot', label: 'Pivot', help: 'Rows nest, with a subtotal at every breakdown level. Subtotals are computed by the query, not summed from the rows below them.' },
  { id: 'flat', label: 'Ungrouped', help: 'One row per combination of breakdowns, with no subtotals.' },
];

const n = (v) => (v == null ? '—' : Number(v).toLocaleString('en-IN'));
const inr = (v, dp = 2) => (v == null ? '—'
  : `₹${Number(v).toLocaleString('en-IN', { minimumFractionDigits: dp, maximumFractionDigits: dp })}`);

const fmtCell = (key, v) => {
  if (v == null) return '—';
  if (key === 'spend') return inr(v);
  if (key === 'cpp') return inr(v, 4);
  if (key === 'cpm' || key === 'cpm_all') return inr(v);
  if (key === 'empty_rate' || key === 'coverage') return `${v}%`;
  return n(v);
};

// Same day format as BM's status strip, and the same clock format for the
// refresh time — an operator reading both screens should not have to translate.
const fmtDay = (iso) => {
  const [y, m, d] = String(iso || '').split('-').map(Number);
  return y && m && d
    ? new Date(y, m - 1, d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
    : '—';
};
const fmtClock = (ts) => (ts
  ? new Date(ts).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })
  : 'never');

const fmtKey = (dim, v) => {
  if (v == null) return null;
  if (dim === 'hour') return `${String(v).padStart(2, '0')}:00`;
  if (dim === 'day' || dim === 'week' || dim === 'month') return String(v).slice(0, 10);
  return String(v);
};

function InfoDot({ title }) {
  return (
    <span className={s.dot} tabIndex={0} role="img" aria-label={title} title={title}>
      <Info size={11} aria-hidden="true" />
    </span>
  );
}

// ── the tree ────────────────────────────────────────────────────────────────
//
// ASSEMBLY ONLY. Every number on every node came from the server; the client
// nests rows and orders them and never adds two of them together. Rows arrive
// ordered by depth ascending, so a parent is always in the map before its
// children look for it — and a truncated response therefore loses leaves rather
// than orphaning them.
function buildTree(rows, dims) {
  const pathAt = (r, d) => dims.slice(0, d).map((k) => `${k}=${r.keys[k] ?? ' '}`).join('/');
  const byPath = new Map();
  const roots = [];
  for (const r of rows) {
    const depth = r.depth == null ? dims.length : r.depth;
    const path = pathAt(r, depth);
    // `leaf` is DERIVED, never trusted from the payload: a row at full depth is
    // a leaf by definition, and a missing flag must not make one read as a
    // subtotal.
    const node = { ...r, depth, path, leaf: depth >= dims.length, children: [] };
    byPath.set(path, node);
    const parent = depth > 1 ? byPath.get(pathAt(r, depth - 1)) : null;
    (parent ? parent.children : roots).push(node);
  }
  return roots;
}

function flattenTree(nodes, collapsed, out = []) {
  for (const node of nodes) {
    out.push(node);
    if (node.children.length && !collapsed.has(node.path)) flattenTree(node.children, collapsed, out);
  }
  return out;
}

export default function Pivot() {
  const [params, setParams] = useSearchParams();
  const readList = useCallback(
    (k) => (params.get(k) ? params.get(k).split(',').filter(Boolean) : []), [params]);

  const today = useMemo(() => startOfDay(new Date()), []);
  const range = useMemo(() => ({
    from: params.get('from') || toISO(addDays(today, -29)),
    to: params.get('to') || toISO(today),
  }), [params, today]);

  const breakdowns = useMemo(() => {
    const b = readList('breakdowns');
    return b.length ? b : ['campaign'];
  }, [readList]);
  const measures = useMemo(() => {
    const m = readList('measures');
    return m.length ? m : ['spend', 'plays', 'billable', 'cpm', 'empty_rate'];
  }, [readList]);
  const filters = useMemo(() => ({
    advertisers: readList('advertisers'),
    statuses: readList('statuses'),
    campaigns: readList('campaigns'),
  }), [readList]);
  const q = params.get('q') || '';
  const view = params.get('view') === 'flat' ? 'flat' : 'pivot';

  const patch = useCallback((obj) => {
    const p = new URLSearchParams(params);
    for (const [k, v] of Object.entries(obj)) {
      if (Array.isArray(v)) { if (v.length) p.set(k, v.join(',')); else p.delete(k); }
      else if (v) p.set(k, v); else p.delete(k);
    }
    setParams(p, { replace: true });
  }, [params, setParams]);

  const qs = useMemo(() => {
    const p = new URLSearchParams({
      from: range.from, to: range.to, view,
      breakdowns: breakdowns.join(','), measures: measures.join(','),
    });
    for (const k of ['advertisers', 'statuses', 'campaigns']) {
      if (filters[k].length) p.set(k, filters[k].join(','));
    }
    if (q) p.set('q', q);
    return p.toString();
  }, [range, breakdowns, measures, filters, q, view]);

  const [customizeOpen, setCustomizeOpen] = useState(false);
  const [customizeTab, setCustomizeTab] = useState('breakdowns');
  const [collapsed, setCollapsed] = useState(() => new Set());

  // Collapse state is keyed by a path built from the breakdowns; changing them
  // makes every stored path meaningless, so it is dropped rather than left to
  // collapse an unrelated row that happens to spell the same path.
  const shape = `${breakdowns.join(',')}|${view}`;
  useEffect(() => { setCollapsed(new Set()); }, [shape]);

  // The search box is a DRAFT that debounces into the URL, exactly as BM's
  // does. Every keystroke going straight to the query param would fire a
  // 5,000-row aggregate per character; committing on a 300 ms pause keeps the
  // URL shareable without turning typing into a load test.
  const [draft, setDraft] = useState(q);
  const lastCommitted = useRef(q);
  useEffect(() => {
    // Only accept the URL as the source of truth when it changed from
    // elsewhere (back button, a pasted link) — otherwise our own debounce
    // would fight the field the user is typing in.
    if (q !== lastCommitted.current) { lastCommitted.current = q; setDraft(q); }
  }, [q]);
  useEffect(() => {
    if (draft.trim() === q) return undefined;
    const t = setTimeout(() => { lastCommitted.current = draft.trim(); patch({ q: draft.trim() }); }, 300);
    return () => clearTimeout(t);
  }, [draft, q, patch]);

  const { data, error, loading, reload } = useApi(
    () => api.get(`/insights/pivot?${qs}`).then((r) => r.data?.data), [qs]);

  // The chrome stays mounted across refetches. Unmounting it would steal focus
  // from the search box on the very keystroke that triggered the request, and
  // collapse the page height on every date change.
  const first = loading && !data;
  const vocabulary = data?.vocabulary;
  const dimensions = useMemo(
    () => (vocabulary?.dimensions || []).map((d) => ({ key: d.key, label: DIM_LABEL[d.key] || d.label })),
    [vocabulary],
  );
  const measureDefs = useMemo(
    () => (vocabulary?.measures || []).map((m) => ({ key: m.key, label: m.label })),
    [vocabulary],
  );

  const toggle = (list, v, key, max) => {
    const next = list.includes(v) ? list.filter((x) => x !== v) : [...list, v];
    if (max && next.length > max) return;
    if (!next.length) return;               // never leave the pivot with no axis
    patch({ [key]: next });
  };

  // THE TABLE RENDERS THE SHAPE THE SERVER ECHOED, NOT THE SHAPE IN THE URL.
  // They differ for the length of a refetch, and using the URL's shape against
  // the previous payload's rows renders a column that does not exist in them —
  // every cell of a new breakdown reading "unattributed", every cell of a new
  // measure blank. Both look like findings. Controls follow intent (the URL);
  // the table follows fact (the payload), and flips atomically when it lands.
  const viewBreakdowns = data?.breakdowns?.length ? data.breakdowns : breakdowns;
  const viewMeasures = data?.measures?.length ? data.measures : measures;
  // AN OLDER API ECHOES NO `view`, AND ITS ROWS CARRY NO depth OR leaf. During a
  // deploy window a new client is served against exactly that, and trusting the
  // URL instead of the payload rendered EVERY row as a subtotal — a table of
  // bold, tinted totals with no leaves under them. Found by pointing the browser
  // checks at a server that had not been redeployed yet.
  //
  // The payload decides, as everywhere else on this screen: no echo means flat,
  // whatever the URL asked for.
  const viewMode = data ? (data.view || 'flat') : view;

  const rows = useMemo(() => data?.rows || [], [data]);
  const visible = useMemo(() => {
    if (!rows.length) return [];
    if (viewMode === 'flat') return rows.map((r, i) => ({ ...r, path: `f${i}`, children: [] }));
    return flattenTree(buildTree(rows, viewBreakdowns), collapsed);
  }, [rows, viewBreakdowns, viewMode, collapsed]);

  const toggleNode = (path) => setCollapsed((prev) => {
    const next = new Set(prev);
    if (next.has(path)) next.delete(path); else next.add(path);
    return next;
  });

  // CSV of exactly what the server returned — no re-aggregation, no rounding,
  // no rates recomputed in the browser. In the pivot view the subtotal rows are
  // exported too, marked by a Level column, because a spreadsheet that silently
  // mixed them into the leaves would double its own total.
  const exportCsv = () => {
    if (!data?.rows) return;
    const cell = (v) => {
      if (v == null) return '';
      const t = String(v);
      return /[",\n]/.test(t) ? `"${t.replace(/"/g, '""')}"` : t;
    };
    // A labelled dimension exports BOTH its name and its id: the name is what a
    // reader recognises, the id is what survives a rename and can be pasted
    // back into a filter. A spreadsheet with only one of them is a dead end.
    const labelled = viewBreakdowns.filter((b) => data.rows.some((r) => r.labels?.[b]));
    const keyCols = viewBreakdowns.flatMap((b) => (labelled.includes(b)
      ? [DIM_LABEL[b] || b, `${DIM_LABEL[b] || b} id`] : [DIM_LABEL[b] || b]));
    const keyVals = (r) => viewBreakdowns.flatMap((b) => {
      const has = Object.prototype.hasOwnProperty.call(r.keys, b);
      const v = !has ? '' : (r.keys[b] == null ? 'unattributed' : r.keys[b]);
      return labelled.includes(b) ? [has ? (r.labels?.[b] || '') : '', v] : [v];
    });
    const lead = viewMode === 'pivot' ? ['Level'] : [];
    const leadVals = (r) => (viewMode === 'pivot'
      ? [r.leaf ? 'row' : `subtotal:${DIM_LABEL[viewBreakdowns[r.depth - 1]] || viewBreakdowns[r.depth - 1]}`]
      : []);
    const cols = [...lead, ...keyCols,
      ...viewMeasures.map((m) => measureDefs.find((x) => x.key === m)?.label || m)];
    const lines = [cols.map(cell).join(',')];
    for (const r of data.rows) {
      lines.push([...leadVals(r), ...keyVals(r), ...viewMeasures.map((m) => r[m])].map(cell).join(','));
    }
    lines.push([...[...lead, ...keyCols].map((_, i) => (i ? '' : 'TOTAL (all matching rows)')),
      ...viewMeasures.map((m) => data.totals?.[m])].map(cell).join(','));
    if (data.truncated) {
      lines.push(cell(`rows truncated at ${vocabulary?.max_rows}; the TOTAL line is over every matching row`));
    }
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `revenue-pivot_${range.from}_${range.to}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(a.href), 0);
  };

  const totals = data?.totals || {};
  const parity = data?.parity;
  const facets = data?.facets;
  const truncated = data?.truncated;
  const source = data?.source;
  const colCount = viewBreakdowns.length + viewMeasures.length;

  return (
    <div className={s.page} data-fill>
      {/* ── row 1: identity, view, actions ────────────────────────────── */}
      <header className={s.head}>
        <h1 className={s.h1}>Revenue pivot</h1>
        <InfoDot title={SUB} />

        <div className={s.viewToggle} role="group" aria-label="Table view">
          {VIEWS.map((v) => (
            <button key={v.id} type="button" title={v.help}
                    aria-pressed={viewMode === v.id}
                    className={`${s.viewBtn} ${viewMode === v.id ? s.viewBtnOn : ''}`}
                    onClick={() => patch({ view: v.id === 'pivot' ? '' : v.id })}>
              {v.label}
            </button>
          ))}
        </div>

        <span className={s.spacer} />

        <button type="button" className={s.btn} onClick={reload} disabled={loading}
                aria-busy={loading}
                title="Re-run the query. The mart itself refreshes hourly, so this re-reads it rather than re-deriving it.">
          <RefreshCw size={13} className={loading ? s.spin : undefined} aria-hidden="true" />
          Refresh
        </button>
        <button type="button" className={s.btn} onClick={exportCsv} disabled={!rows.length}
                title="Download these rows plus the server's totals as CSV. Values are exported exactly as returned — nothing is recomputed in the browser.">
          <Download size={13} aria-hidden="true" />
          Export
        </button>
        <button type="button" aria-pressed={customizeOpen}
                className={`${s.btn} ${customizeOpen ? s.btnOn : ''}`}
                onClick={() => setCustomizeOpen((o) => !o)}
                title="Choose which breakdowns nest the rows and which measures form the columns.">
          <SlidersHorizontal size={13} aria-hidden="true" />
          Customize
        </button>
      </header>

      {/* ── row 2: search, filters, window ────────────────────────────── */}
      <div className={s.controls}>
        <div className={s.searchWrap}>
          <Search size={13} className={s.searchIcon} aria-hidden="true" />
          <input
            type="search" className={s.searchInput} value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Search name, ID or status"
            aria-label="Search rows"
            title="Filters on the SERVER, so the totals and the parity check move with it. Matches campaign name, campaign ID, advertiser and status — not ad set ID, which the parity oracle does not carry."
          />
          {draft && (
            <button type="button" className={s.searchClear} aria-label="Clear search"
                    onClick={() => setDraft('')}>
              <X size={12} />
            </button>
          )}
        </div>

        {facets && (
          <Filters compact facets={facets} value={filters}
                   onChange={(v) => patch(v)} summary={null} />
        )}

        <span className={s.spacer} />
        <DateRange from={range.from} to={range.to}
                   onChange={(f, t) => patch({ from: f, to: t })} />
      </div>

      {/* OUT OF PARITY IS A STOP CONDITION, so it sits above the table rather
          than in the quiet strip below it. In parity, it belongs in the strip:
          a green banner on every page load is one people stop reading. */}
      {parity && !parity.in_parity && (
        <div className={s.parityBad}>
          <Pill tone="bad">OUT OF PARITY</Pill>
          <span className={s.parityText}>
            These numbers DISAGREE with the live derivation on closed days:
            {' '}&Delta;{n(parity.plays.delta)} plays, &Delta;{inr(parity.spend.delta)},
            {' '}&Delta;{n(parity.billable.delta)} advertiser-facing. Do not use this view
            until it is resolved.
          </span>
        </div>
      )}
      {data?.rollup_agrees === false && (
        <div className={s.parityBad}>
          <Pill tone="bad">SUBTOTALS DISAGREE</Pill>
          <span className={s.parityText}>
            The grouped query&rsquo;s grand total does not match the ungrouped one over the
            same rows. Switch to Ungrouped and report this.
          </span>
        </div>
      )}
      {truncated && (
        <div className={s.truncated}>
          Showing the first {n(vocabulary?.max_rows)} rows. The totals row is computed over
          EVERY matching row, not the ones displayed, so it stays correct — add a filter or
          drop a breakdown to see them all.
        </div>
      )}

      {/* ── the frame: table left, customize right ────────────────────── */}
      <div className={s.body}>
        <div className={s.main}>
          <div className={`${s.tableWrap} ${loading && data ? s.stale : ''}`}>
            {first && <Loading what="the pivot" />}
            {!first && error && <ErrorState error={error} onRetry={reload} />}
            {!first && !error && (
              <table className={s.table}>
                <thead>
                  <tr>
                    {viewBreakdowns.map((b) => (
                      <th key={b} className={s.hKey}>
                        {DIM_LABEL[b] || b}
                        <InfoDot title={DIM_HELP[b]} />
                      </th>
                    ))}
                    {viewMeasures.map((m) => (
                      <th key={m} className={s.hNum}>
                        {measureDefs.find((x) => x.key === m)?.label || m}
                        <InfoDot title={MEA_HELP[m]} />
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {visible.map((node) => {
                    const isSub = viewMode === 'pivot' && !node.leaf;
                    const open = !collapsed.has(node.path);
                    return (
                      <tr key={node.path} className={isSub ? s.subRow : undefined}>
                        {viewBreakdowns.map((b, i) => {
                          const show = viewMode === 'flat' || i === node.depth - 1;
                          if (!show) return <td key={b} className={s.key} />;
                          const label = node.labels?.[b] || fmtKey(b, node.keys[b]);
                          const id = node.keys[b];
                          return (
                            <th key={b} scope="row" className={s.key}
                                title={node.labels?.[b] ? `${node.labels[b]} · ${id}` : undefined}>
                              {/* NESTING IS BY COLUMN, not by pixel padding: a
                                  row writes its label in the column matching its
                                  depth, so level 2 is already offset by the width
                                  of level 1. BM does the same. The only alignment
                                  needed is the chevron gutter, so a group and a
                                  childless group at the same level line up. */}
                              <span className={s.keyBody}>
                                {viewMode === 'pivot' && node.children.length > 0 ? (
                                  <button type="button" className={s.chev}
                                          aria-expanded={open}
                                          aria-label={`${open ? 'Collapse' : 'Expand'} ${label || 'unattributed'}`}
                                          onClick={() => toggleNode(node.path)}>
                                    {open ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                                  </button>
                                ) : viewMode === 'pivot' && i === 0 && viewBreakdowns.length > 1
                                  ? <span className={s.chevGap} /> : null}
                                {label == null
                                  ? <span className={s.null} title="Revenue whose campaign no longer resolves — kept rather than dropped, so the totals stay whole.">unattributed</span>
                                  : <span className={s.keyName}>{label}</span>}
                                {node.labels?.[b] && (
                                  <span className={s.keyId}>{String(id).slice(0, 8)}</span>
                                )}
                              </span>
                            </th>
                          );
                        })}
                        {viewMeasures.map((m) => (
                          <td key={m} className={s.num}>{fmtCell(m, node[m])}</td>
                        ))}
                      </tr>
                    );
                  })}
                  {!visible.length && (
                    <tr><td colSpan={colCount} className={s.empty}>
                      {q
                        ? <>Nothing in this window matches &ldquo;{q}&rdquo;.{' '}
                          <button type="button" className={s.linkBtn} onClick={() => setDraft('')}>
                            Clear the search
                          </button> to see every row.</>
                        : 'Nothing matches this window and these filters.'}
                    </td></tr>
                  )}
                </tbody>
                <tfoot>
                  <tr>
                    <th colSpan={viewBreakdowns.length} scope="row" className={s.totalLabel}>
                      Total
                      <InfoDot title="Computed over every matching row by its own query — not summed from the rows on screen, so it stays correct even when the view is truncated or collapsed." />
                    </th>
                    {viewMeasures.map((m) => (
                      <td key={m} className={`${s.num} ${s.totalCell}`}>{fmtCell(m, totals[m])}</td>
                    ))}
                  </tr>
                </tfoot>
              </table>
            )}
          </div>

          {/* STATUS STRIP — window, what reconciled, what is still moving, and
              when the numbers were last derived. BM puts the same facts here. */}
          {data && (
            <div className={s.strip}>
              <span title="Every bucket on this screen is an IST calendar bucket, the same one billing settles on.">
                {fmtDay(range.from)} – {fmtDay(range.to)} · Indian Standard Time
              </span>
              <span title="Rows currently rendered, out of what the query returned. Collapsing a group hides rows here and changes nothing below.">
                {n(visible.length)} of {n(rows.length)}{truncated ? '+' : ''} rows ·
                {' '}{viewBreakdowns.length} breakdown{viewBreakdowns.length > 1 ? 's' : ''} ·
                {' '}{viewMeasures.length} measure{viewMeasures.length > 1 ? 's' : ''}
              </span>
              {data.regime && (
                <span title={data.regime.degraded
                  || 'Display money (Accrued and the rates built on it) is priced by the regime that governed each day: blanket accrual before 1 Aug 2026, presence-verified money from it. The parity check below stays on the blanket accrual — books are never filtered.'}>
                  {data.regime.degraded ? 'Blanket accrual — presence split arrives with V353' : data.regime.note}
                </span>
              )}
              {parity?.in_parity && (
                <span className={s.ok}
                      title="Closed days in this window match signals.company_day_revenue exactly — same plays, same rupees, same advertiser-facing count (blanket accrual on both sides — the display regime never filters books). Checked on every request, not on a schedule.">
                  Reconciled · {n(parity.plays.mart)} plays, {inr(parity.spend.mart)} on closed days
                </span>
              )}
              {parity?.open_day && (parity.open_day.plays !== 0 || parity.open_day.spend !== 0) && (
                <span title="Today is still accruing, so a materialised table necessarily trails the live source between refreshes. This amount IS included in the rows and totals above, and excluded from the parity assertion — a property of materialisation, not a defect.">
                  Today +{n(parity.open_day.plays)} plays · {inr(parity.open_day.spend)}
                </span>
              )}
              <span className={`${s.updated} ${source?.stale ? s.staleText : ''}`}
                    title={source?.stale
                      ? 'The hourly refresh has not run in over six hours — four missed runs. These numbers are stale; treat them as a floor.'
                      : 'When the mart was last refreshed, not when this page loaded. The refresh runs hourly.'}>
                {source?.stale ? 'STALE · ' : ''}Updated {fmtClock(source?.refreshed_at)}
              </span>
            </div>
          )}
        </div>

        {customizeOpen && vocabulary && (
          <CustomizePanel
            tab={customizeTab}
            onTab={setCustomizeTab}
            dimensions={dimensions}
            measures={measureDefs}
            dimensionSections={DIM_SECTIONS}
            measureSections={MEA_SECTIONS}
            selectedBreakdowns={breakdowns}
            selectedMeasures={measures}
            onToggleBreakdown={(k) => toggle(breakdowns, k, 'breakdowns', vocabulary.max_breakdowns)}
            onToggleMeasure={(k) => toggle(measures, k, 'measures')}
            onClose={() => setCustomizeOpen(false)}
            maxBreakdowns={vocabulary.max_breakdowns}
            help={{ ...DIM_HELP, ...MEA_HELP }}
            footNote={(
              <>
                Accrued, before GST; rates recomputed from each row&rsquo;s own sums.{' '}
                <NavLink to="/v2/insights" className={s.link}>Company revenue</NavLink>
              </>
            )}
          />
        )}
      </div>
    </div>
  );
}
